import React, { FC, useContext } from 'react';

import styles from './styles.module.css';
import { DriverType, VehicleType } from '@clearcover/core-app-types';
import {
  formatDateShort,
  formatMoney,
  combineStrings,
  formatPhoneNumber,
  EMDASH,
} from 'Shared/utils';
import { formatDateMonthYear } from 'Shared/utils/date';
import { CardDataSection } from 'Shared/components/Card/CardDataSection';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { Asset, CheckmarkIcon, CloseIcon } from '@clearcover/atlas';
import { DriverReportTable } from '../PolicyDriversSection/DriverForm/DriverReportTable';
import { PolicyContext } from 'Policies/utils';
import { RequiredDataTestIdProp } from 'Shared/utils/props/dataTestIdProp';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { DriverInfoOptionsType } from '../PolicyDriversSection/DriverForm';

const showIcon = (challenge?: string, collapsed: boolean = false) => {
  const checked = challenge !== 'removed' && !collapsed;

  return (
    <Asset
      icon={checked ? CheckmarkIcon : CloseIcon}
      description={'Rated'}
      height={12}
      width={12}
      className={checked ? styles.checkmarkIcon : styles.closeIcon}
    />
  );
};

type PolicyDriverDetailsType = RequiredDataTestIdProp & {
  driver: DriverType;
  vehicles: Array<VehicleType>;
  driverInfoOptions: DriverInfoOptionsType;
};

const userReportedStatusLabels = {
  active: 'Active',
  cancelled: 'Cancelled',
  expired: 'Expired',
  idOnly: 'ID only',
  international: 'International',
  learner: 'Learner',
  neverBeenLicensed: 'Never Licensed',
  restricted: 'Restricted',
  revoked: 'Revoked',
  suspended: 'Suspended',
  other: 'Other',
};

const reportStatusLabels = {
  valid: 'Valid',
  suspended: 'Suspended',
  revoked: 'Revoked',
  expired: 'Expired',
  cancelled: 'Cancelled',
  denied: 'Denied',
  limited: 'Limited',
  military: 'Military',
  idOnly: 'ID only',
  disqualified: 'Disqualified',
  eligible: 'Eligible',
  deceased: 'Deceased',
  unavailable: 'Unavailable',
  noValidLicense: 'No Valid License',
  unknown: 'Unknown',
};

const userReportedStatusLabel = (
  status: string | undefined | null
): string | null => {
  if (!status) {
    return null;
  }

  return (
    userReportedStatusLabels[status as keyof typeof userReportedStatusLabels] ||
    status
  );
};

const reportStatusLabel = (
  status: string | undefined | null
): string | null => {
  if (!status) {
    return null;
  }

  return (
    reportStatusLabels[status as keyof typeof reportStatusLabels] || status
  );
};

export const PolicyDriverDetails: FC<PolicyDriverDetailsType> = ({
  driver: {
    person,
    driversLicense,
    accidents,
    sr22s,
    violations,
    relationToApplicant,
    attributes,
    military,
    student,
    priorCarrier,
    priorPolicies,
    assignedVehicles,
    contactInfo,
    cfrs,
    policyPartyRelationship,
    coverageHistory,
  },
  vehicles,
  driverInfoOptions,
  'data-testid': dataTestId,
}) => {
  const { details } = useContext(PolicyContext);

  const accidentsHasClassificationEnabled = !!accidents[0]?.classifications;

  const accidentsToShow = accidentsHasClassificationEnabled
    ? accidents.filter(
        ({ claimStatus, classifications }) =>
          claimStatus !== 'Collapsed' && !classifications?.lendingLoss
      )
    : accidents;

  const isFloridaRegion = details.region === 'US-FL';
  const isPrimaryDriver = policyPartyRelationship === 'named_insured';

  const temporaryHiddenColumnsForBackwardsCompatibility = [
    'Military Rank',
    'Currently Deployed',
    ...(isFloridaRegion && isPrimaryDriver ? [] : ['INSURED IN PAST 5 YEARS']),
    ...(details.showEducation ? [] : ['Education Level']),
    ...(details.showOccupation ? [] : ['Occupation']),
  ];

  const attributesToShow = [
    ...Object.values(attributes).filter(attr => attr.value),
    {
      title: 'INSURED IN PAST 5 YEARS',
      value:
        coverageHistory?.coveredWithinLookbackPeriod ===
        'coveredWithinLast5Years'
          ? 'Yes'
          : 'No',
    },
  ];

  return (
    <div data-testid={dataTestId} className={styles.detailsContainer}>
      <CardDataSection caption="General Info" id="general-info">
        <CardSectionInfoPairs
          headers={[
            'Gender',
            'Marital status',
            'Date of Birth',
            ...(!isPrimaryDriver ? ['Relation to Applicant'] : []),
          ]}
          data={[
            driverInfoOptions?.gender?.[person.gender as string] ??
              person.gender ??
              '—',
            driverInfoOptions?.maritalStatus?.[
              person?.maritalStatus as string
            ] ??
              person?.maritalStatus ??
              '—',
            formatDateShort(person.dateOfBirth),
            ...(!isPrimaryDriver
              ? [
                  driverInfoOptions?.relationToApplicant?.[
                    relationToApplicant as string
                  ] ??
                    relationToApplicant ??
                    '—',
                ]
              : []),
          ]}
        />
      </CardDataSection>
      {isPrimaryDriver && (
        <CardDataSection caption="Contact Info" id="Contact Info">
          <CardSectionInfoPairs
            dataFontStyle="none"
            headers={['Home Phone', 'Cell Phone', 'Email']}
            data={[
              contactInfo.homePhone
                ? formatPhoneNumber(contactInfo.homePhone)
                : EMDASH,
              contactInfo.cellPhone
                ? formatPhoneNumber(contactInfo.cellPhone)
                : EMDASH,
              contactInfo.email,
            ]}
          />
        </CardDataSection>
      )}
      <CardDataSection caption="Attributes" id="attributes">
        <CardSectionInfoPairs
          hiddenColumns={temporaryHiddenColumnsForBackwardsCompatibility}
          headers={attributesToShow.map(attr => attr.title)}
          data={attributesToShow.map(attr => attr.value)}
        />
      </CardDataSection>
      {military && details.showMilitarySection && (
        <CardDataSection caption="Military" id="military">
          <CardSectionInfoPairs
            headers={Object.values(military).map(attr => attr.title)}
            data={Object.values(military).map(attr =>
              attr.value ? attr.value : EMDASH
            )}
          />
        </CardDataSection>
      )}
      {student && details.showStudentSection && (
        <CardDataSection caption="Student" id="student">
          <CardSectionInfoPairs
            headers={Object.values(student).map(attr => attr.title)}
            data={Object.values(student).map(attr =>
              attr.value ? attr.value : EMDASH
            )}
          />
        </CardDataSection>
      )}
      {/* <CardDataSection caption="Discounts" id="discounts" lastSection={true}>
        <CardSectionInfoPairs
          headers={discounts.map(discount => discount.title)}
          data={discounts.map(discount => discount.value)}
        />
      </CardDataSection> */}
      {details.region === 'US-VA' ? (
        <CardDataSection caption="Assigned Vehicles" id="assigned-vehicles">
          <div className={styles.combined}>
            {vehicles
              .filter(v => assignedVehicles.includes(v.identifier))
              .map(vehicle =>
                combineStrings([vehicle.year, vehicle.make, vehicle.model])
              )
              .join(', ')}
          </div>
        </CardDataSection>
      ) : null}
      <CardDataSection
        caption="Prior carrier"
        id="prior-carrier"
        lastSection={false}
      >
        {!isFloridaRegion && (
          <DriverReportTable
            data-testid="prior-carrier-reports-table"
            headers={[
              'RATED',
              'SOURCE',
              'CURRENTLY INSURED?',
              'CURRENT CARRIER',
              'DURATION',
              'BI LIMITS',
            ]}
            tableRows={[
              {
                data: [
                  showIcon(priorCarrier.challenge),
                  <div>{priorCarrier.source}</div>,
                  <div>{priorCarrier.wasPreviouslyInsured ? 'Yes' : 'No'}</div>,
                  <div>{priorCarrier.carrier}</div>,
                  <div>{priorCarrier.duration}</div>,
                  <div>{priorCarrier.bodyInjuryLimits}</div>,
                ],
                challenge: priorCarrier.challenge,
              },
            ]}
          />
        )}
        {isFloridaRegion && (
          <DriverReportTable
            autoColumnAlignment
            data-testid="prior-carrier-reports-table"
            headers={[
              'SOURCE',
              'CARRIER',
              'POLICY NUMBER',
              'EFFECTIVE DATE',
              'END',
              'AM BEST NUMBER',
              'STATE',
              'BI LIMITS',
              'CSL LIMIT',
              'PIP LIMIT',
            ]}
            tableRows={
              priorPolicies
                ?.filter(priorPolicy => priorPolicy.challenge !== 'removed')
                ?.map((priorPolicy, index) => {
                  return {
                    data: [
                      <div data-testid={`priorPolicy-${index}-source`}>
                        {priorPolicy.source}
                      </div>,
                      <div
                        data-testid={`priorPolicy-${index}-currentCarrierName`}
                      >
                        {priorPolicy.currentCarrierName}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-policyNumber`}>
                        {priorPolicy.policyNumber}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-startDate`}>
                        {formatDateMonthYear(priorPolicy.startDate)}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-endDate`}>
                        {formatDateMonthYear(priorPolicy.endDate)}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-amBestNumber`}>
                        {priorPolicy.amBestNumber}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-state`}>
                        {priorPolicy.state && priorPolicy.state.includes('-')
                          ? priorPolicy.state.split('-')[1]
                          : priorPolicy.state || '-'}
                      </div>,
                      <div data-testid={`priorPolicy-${index}-bodilyInjury`}>
                        {priorPolicy.coverages.bodilyInjury || '-'}
                      </div>,
                      <div
                        data-testid={`priorPolicy-${index}-combinedSingleLimit`}
                      >
                        {priorPolicy.coverages.combinedSingleLimit || '-'}
                      </div>,
                      <div
                        data-testid={`priorPolicy-${index}-personalInjuryProtection`}
                      >
                        {priorPolicy.coverages.personalInjuryProtection || '-'}
                      </div>,
                    ],
                  };
                }) ?? []
            }
          />
        )}
      </CardDataSection>
      <span className="fs-exclude">
        <CardDataSection caption="Driver’s License" id="drivers-license">
          <DriverReportTable
            data-testid="drivers-license-reports-table"
            headers={[
              'Rated',
              'Source',
              'License Number',
              'License State',
              'License Status',
            ]}
            tableRows={[
              {
                data: [
                  showIcon(driversLicense.challenge),
                  <div>{driversLicense.source}</div>,
                  <div>{driversLicense.number ?? EMDASH}</div>,
                  <div>{driversLicense.state?.toUpperCase() ?? EMDASH}</div>,
                  <div>
                    {reportStatusLabel(driversLicense.reportStatus) ??
                      userReportedStatusLabel(
                        driversLicense.userReportedStatus
                      ) ??
                      driversLicense.status}
                  </div>,
                ],
                challenge: driversLicense.challenge,
              },
            ]}
          />
        </CardDataSection>
      </span>

      {!details.showDriverCfrs ? (
        <CardDataSection caption="SR-22" id="sr22s">
          <DriverReportTable
            // eslint-disable-next-line testing-library/consistent-data-testid
            data-testid="sr22s-reports-table"
            headers={['Rated', 'Source', 'Has SR-22']}
            tableRows={sr22s.map((sr22, index) => {
              return {
                data: [
                  <div data-testid={`sr22-${index}-rated`}>
                    {showIcon(sr22.challenge)}
                  </div>,
                  <div data-testid={`sr22-${index}-source`}>{sr22.source}</div>,
                  <div data-testid={`sr22-${index}-has`}>Yes</div>,
                ],
                challenge: sr22.challenge,
              };
            })}
          />
        </CardDataSection>
      ) : (
        <CardDataSection caption="SR-22/FR-44" id="cfrs">
          <DriverReportTable
            // eslint-disable-next-line testing-library/consistent-data-testid
            data-testid="cfr-reports-table"
            headers={[
              'RATED',
              'SOURCE',
              'HAS SR-22/FR-44',
              'CASE NUMBER',
              'ISSUED STATE',
            ]}
            tableRows={cfrs.map(({ source, type, caseNumber, state }) => {
              const stateName =
                STATE_OPTIONS?.[state as keyof typeof STATE_OPTIONS];
              return {
                data: [
                  <span>{showIcon('CHECKED')}</span>, // "CHECKED" -> Always rendering "Checked" Icon
                  <span>{source}</span>,
                  <span>{type || 'None'}</span>,
                  <span>{caseNumber}</span>,
                  <span>{stateName || '-----'}</span>,
                ],
              };
            })}
          />
        </CardDataSection>
      )}

      <CardDataSection caption="Violations" id="violations">
        <DriverReportTable
          data-testid="violations-reports-table"
          headers={[
            'Rated',
            'Source',
            'Violation Date',
            'Conviction Date',
            'Description',
          ]}
          tableRows={
            violations.length
              ? violations.map((violation, index) => ({
                  data: [
                    showIcon(violation.challenge, violation.collapsed),
                    <div data-testid={`violation-${index}-source`}>
                      {violation.source}
                    </div>,
                    <div
                      data-testid={`violation-${index}-date`}
                      className={styles.first}
                    >
                      {formatDateShort(violation.violationDate)}
                    </div>,
                    <div data-testid={`violation-${index}-conviction`}>
                      {formatDateShort(violation.convictionDate)}
                    </div>,
                    <div data-testid={`violation-${index}-description`}>
                      {violation.descriptionOptions[violation.description]}
                    </div>,
                  ],
                  challenge: violation.challenge,
                }))
              : []
          }
        />
      </CardDataSection>
      <CardDataSection caption="Accidents" id="accidents" lastSection={true}>
        <DriverReportTable
          data-testid="accidents-reports-table"
          isClaimsList
          headers={
            accidentsHasClassificationEnabled
              ? [
                  'Claim Status',
                  'Source',
                  'Accident Date',
                  'Type',
                  'Loss Amount',
                  'Fault',
                ]
              : [
                  'Claim Status',
                  'Source',
                  'Accident Date',
                  'Fault',
                  'Total Loss',
                ]
          }
          tableRows={
            accidentsToShow.length
              ? accidentsToShow.map((accident, index) => ({
                  data: accidentsHasClassificationEnabled
                    ? [
                        <div data-testid={`accident-${index}-status`}>
                          {accident.claimStatus}
                        </div>,
                        <div data-testid={`accident-${index}-source`}>
                          {accident.source}
                        </div>,
                        <div data-testid={`accident-${index}-date`}>
                          {formatDateShort(accident.accidentDate)}
                        </div>,
                        <div data-testid={`accident-${index}-type`}>
                          {accident.classifications
                            ?.claimClassificationTypeV2 || '-'}
                        </div>,
                        <div data-testid={`accident-${index}-loss`}>
                          {formatMoney(accident.damageAmount)}
                        </div>,
                        <div data-testid={`accident-${index}-fault`}>
                          {accident.isAtFault ? 'At-Fault' : 'Not At-Fault'}
                        </div>,
                      ]
                    : [
                        <div data-testid={`accident-${index}-status`}>
                          {accident.claimStatus}
                        </div>,
                        <div data-testid={`accident-${index}-source`}>
                          {accident.source.toUpperCase()}
                        </div>,
                        <div data-testid={`accident-${index}-date`}>
                          {formatDateShort(accident.accidentDate)}
                        </div>,
                        <div data-testid={`accident-${index}-fault`}>
                          {accident.isAtFault ? 'At-Fault' : 'Not At-Fault'}
                        </div>,
                        <div data-testid={`accident-${index}-loss`}>
                          {formatMoney(accident.damageAmount)}
                        </div>,
                      ],
                  challenge: accident.challenge,
                }))
              : []
          }
        />
      </CardDataSection>
    </div>
  );
};
